// MACHINERY V2 (Stroje) — decommissioned banner + Restore (M2.4).
//
// Spec: MACHINE_PROFILE_REDESIGN_DATA_PACKAGE.md §1 — "Decommission banner
// present in data pkg, NOT shown in artboard — must carry" (ticket §5 #1). So
// this surface has no designer reference: it is rebuilt in the v2 idiom
// (amber-tinted card, mono date line, italic quoted reason) from the live
// banner's content, per the §9 D4 consistency rule.
//
// THE BANNER OWNS RESTORE (§1: "rendered only inside banner"), which is why
// useProfileActions filters the `restore` entry out of the kebab — one
// affordance, not two. The gate is still the shared one: `onRestore` is null
// unless buildFleetActions produced a restore entry for this user, and the
// backend enforces Write regardless.
//
// The three content fields are all conditional except the title:
// decommissionedDate -> `decommissionedOn {date}`, decommissionReason -> italic
// quoted line. Both come straight off the DTO — no derivation.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { Machine } from "../../models/models";

const WarningGlyph: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 3.5 21 19.5H3z" />
    <path d="M12 9.5v4.5" />
    <path d="M12 17h.01" />
  </svg>
);

export interface ProfileDecommissionBannerProps {
  machine: Machine;
  /** Null when the user has no restore entry — the button then does not render. */
  onRestore: (() => void) | null;
}

const ProfileDecommissionBanner: React.FC<ProfileDecommissionBannerProps> = ({
  machine,
  onRestore,
}) => {
  const { translate, getCurrentLanguage } = useTranslation("myMachinery");

  return (
    <div
      style={{
        padding: "12px 14px",
        borderRadius: 10,
        background: "color-mix(in oklab, var(--amber) 10%, var(--card))",
        border: "1px solid color-mix(in oklab, var(--amber) 30%, var(--border))",
        borderLeft: "3px solid var(--amber)",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        minWidth: 0,
      }}
    >
      <span style={{ color: "var(--amber)", flexShrink: 0, display: "inline-flex" }}>
        <WarningGlyph />
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--ink)" }}>
          {translate("myMachinery:decommissionedBannerTitle")}
        </div>
        {machine.decommissionedDate && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--muted)",
              marginTop: 2,
            }}
          >
            {translate("myMachinery:decommissionedOn", {
              date: new Date(machine.decommissionedDate).toLocaleDateString(
                getCurrentLanguage()
              ),
            })}
          </div>
        )}
        {machine.decommissionReason && (
          <div
            style={{
              fontSize: 12.5,
              fontStyle: "italic",
              color: "var(--muted)",
              marginTop: 3,
            }}
          >
            {"„" + machine.decommissionReason + "“"}
          </div>
        )}
      </div>

      {onRestore && (
        <button
          type="button"
          className="btn"
          style={{ flexShrink: 0 }}
          onClick={onRestore}
        >
          {translate("myMachinery:restore")}
        </button>
      )}
    </div>
  );
};

export default ProfileDecommissionBanner;
